// src/components/goal-settings/KpaCreateModal.jsx
import React, { useEffect, useState } from 'react';
import { listTimeframes } from '../../api/lookups';

// Small helper to keep numbers safe
const toNum = (v, def = 0) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : def;
};

/**
 * Props:
 * - show/open/visible: boolean — any of these will open the modal
 * - contractId: number (injected by parent; used when creating)
 * - onClose(): void
 * - onCreated(form): Promise|void  // parent (KpaList) calls createKpa({ ...form, contract_id })
 *
 * Form shape returned to parent:
 *   { name, weight, timeframe_id (nullable), alignment, description }
 */
export default function KpaCreateModal({
                                           show, open, visible,
                                           contractId,
                                           onClose,
                                           onCreated,
                                       }) {
    const isOpen = !!(show ?? open ?? visible);

    const [name, setName] = useState('');
    const [weight, setWeight] = useState('');
    const [timeframeId, setTimeframeId] = useState('');
    const [alignment, setAlignment] = useState('');
    const [description, setDescription] = useState('');

    const [timeframes, setTimeframes] = useState([]);
    const [loadingTF, setLoadingTF] = useState(false);
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState('');

    // Fetch timeframes whenever modal opens
    useEffect(() => {
        if (!isOpen) return;
        (async () => {
            try {
                setLoadingTF(true);
                setErr('');
                const rows = await listTimeframes();
                setTimeframes(rows);
                // Pick first timeframe by default (optional)
                if (rows.length && !timeframeId) setTimeframeId(String(rows[0].id));
            } catch (e) {
                setTimeframes([]);
                // We still render a dropdown (empty) so the UI is consistent.
            } finally {
                setLoadingTF(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    // Reset the form when opened
    useEffect(() => {
        if (!isOpen) return;
        setName('');
        setWeight('');
        setAlignment('');
        setDescription('');
        setErr('');
        // don't force timeframeId here; allow the fetch effect to set it
    }, [isOpen]);

    const submit = async (e) => {
        e?.preventDefault?.();
        if (!name.trim()) return setErr('Name is required.');
        if (!weight || !Number.isFinite(Number(weight))) return setErr('Weight must be a number.');

        try {
            setSaving(true);
            setErr('');
            const payload = {
                name: name.trim(),
                weight: toNum(weight),
                timeframe_id: timeframeId ? Number(timeframeId) : null,
                alignment: alignment?.trim() || null,
                description: description?.trim() || null,
            };
            await onCreated?.(payload);   // parent injects contract_id & refreshes
            onClose?.();
        } catch (e2) {
            setErr(e2?.response?.data?.message || 'Failed to create KPA.');
        } finally {
            setSaving(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="modal show" style={{ display: 'block', background: 'rgba(0,0,0,.4)' }} aria-modal="true" role="dialog">
            <div className="modal-dialog modal-md">
                <div className="modal-content">
                    <div className="modal-header py-2">
                        <h6 className="modal-title">Add KPA</h6>
                        <button type="button" className="btn-close" onClick={onClose} />
                    </div>

                    <form onSubmit={submit}>
                        <div className="modal-body">
                            {err && <div className="alert alert-danger py-1">{err}</div>}

                            <div className="mb-2">
                                <label className="form-label mb-1">Name <span className="text-danger">*</span></label>
                                <input
                                    className="form-control form-control-sm"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder="e.g., Deliver 3 product releases"
                                    autoFocus
                                />
                            </div>

                            <div className="row">
                                <div className="col-6">
                                    <div className="mb-2">
                                        <label className="form-label mb-1">Weight <span className="text-danger">*</span></label>
                                        <input
                                            className="form-control form-control-sm"
                                            type="number"
                                            step="0.01"
                                            value={weight}
                                            onChange={(e) => setWeight(e.target.value)}
                                            placeholder="e.g., 30"
                                        />
                                    </div>
                                </div>

                                <div className="col-6">
                                    <div className="mb-2">
                                        <label className="form-label mb-1">Timeframe</label>
                                        <select
                                            className="form-select form-select-sm"
                                            disabled={loadingTF}
                                            value={timeframeId}
                                            onChange={(e) => setTimeframeId(e.target.value)}
                                        >
                                            {timeframes.length === 0 && (
                                                <option value="">— No timeframes —</option>
                                            )}
                                            {timeframes.map(tf => (
                                                <option key={tf.id} value={tf.id}>{tf.name}</option>
                                            ))}
                                        </select>
                                        {timeframes.length === 0 && (
                                            <small className="text-muted">
                                                No timeframes found. Seed <code>timeframes</code> table or expose <code>GET /api/timeframes</code>.
                                            </small>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="mb-2">
                                <label className="form-label mb-1">Alignment</label>
                                <input
                                    className="form-control form-control-sm"
                                    value={alignment}
                                    onChange={(e) => setAlignment(e.target.value)}
                                    placeholder="How this aligns with strategy"
                                />
                            </div>

                            <div className="mb-1">
                                <label className="form-label mb-1">Description</label>
                                <textarea
                                    className="form-control form-control-sm"
                                    rows={3}
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    placeholder="Optional details..."
                                />
                            </div>
                        </div>

                        <div className="modal-footer py-2">
                            <button type="button" className="btn btn-sm btn-light" onClick={onClose} disabled={saving}>Cancel</button>
                            <button type="submit" className="btn btn-sm btn-primary" disabled={saving}>
                                {saving ? 'Saving…' : 'Create KPA'}
                            </button>
                        </div>
                    </form>

                </div>
            </div>
        </div>
    );
}